import {
  collection,
  addDoc,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  Timestamp
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../config/firebase';
import { Personagem, PersonagemFormData } from '../types/personagem';

const COLLECTION = 'personagens';

const uploadFoto = async (personagemId: string, foto: File): Promise<{ fotoUrl: string; fotoPath: string }> => {
  const fotoPath = `personagens/${personagemId}-${Date.now()}-${foto.name}`;
  const storageRef = ref(storage, fotoPath);
  await uploadBytes(storageRef, foto);
  const fotoUrl = await getDownloadURL(storageRef);
  return { fotoUrl, fotoPath };
};

const deleteFoto = async (fotoPath?: string): Promise<void> => {
  if (!fotoPath) return;
  try {
    await deleteObject(ref(storage, fotoPath));
  } catch (error) {
    console.error('Erro ao remover foto antiga do personagem:', error);
  }
};

export const personagemService = {
  async getPersonagens(): Promise<Personagem[]> {
    try {
      const q = query(collection(db, COLLECTION), orderBy('nome'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data()
      })) as Personagem[];
    } catch (error) {
      console.error('Erro ao buscar personagens:', error);
      throw error;
    }
  },

  async addPersonagem(data: PersonagemFormData): Promise<string> {
    try {
      const docRef = await addDoc(collection(db, COLLECTION), {
        nome: data.nome,
        categoria: data.categoria || '',
        descricao: data.descricao || '',
        precoBase: data.precoBase || '',
        ativo: data.ativo,
        fotoUrl: '',
        fotoPath: '',
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });

      if (data.foto) {
        const { fotoUrl, fotoPath } = await uploadFoto(docRef.id, data.foto);
        await updateDoc(doc(db, COLLECTION, docRef.id), { fotoUrl, fotoPath });
      }

      return docRef.id;
    } catch (error) {
      console.error('Erro ao adicionar personagem:', error);
      throw error;
    }
  },

  async updatePersonagem(id: string, data: PersonagemFormData, currentFotoPath?: string): Promise<void> {
    try {
      const updates: Record<string, any> = {
        nome: data.nome,
        categoria: data.categoria || '',
        descricao: data.descricao || '',
        precoBase: data.precoBase || '',
        ativo: data.ativo,
        updatedAt: Timestamp.now()
      };

      if (data.foto) {
        await deleteFoto(currentFotoPath);
        const { fotoUrl, fotoPath } = await uploadFoto(id, data.foto);
        updates.fotoUrl = fotoUrl;
        updates.fotoPath = fotoPath;
      }

      await updateDoc(doc(db, COLLECTION, id), updates);
    } catch (error) {
      console.error('Erro ao atualizar personagem:', error);
      throw error;
    }
  },

  async setAtivo(id: string, ativo: boolean): Promise<void> {
    try {
      await updateDoc(doc(db, COLLECTION, id), { ativo, updatedAt: Timestamp.now() });
    } catch (error) {
      console.error('Erro ao alterar status do personagem:', error);
      throw error;
    }
  },

  async deletePersonagem(id: string, fotoPath?: string): Promise<void> {
    try {
      await deleteFoto(fotoPath);
      await deleteDoc(doc(db, COLLECTION, id));
    } catch (error) {
      console.error('Erro ao excluir personagem:', error);
      throw error;
    }
  }
};
